import { camelizeKeys, convertAttributesToBoolean, mapKeysToCamelCase } from '@skytech/utils'
import uniqBy from 'lodash/uniqBy'

import { ECOM_ORDER_STATUS } from '@/modules/ecom-policy/constants'
import type {
  Order,
  Product,
  ProductGroup,
  Vendor,
  Customer,
  CustomerHead,
  PolicyOrder,
  PolicyOrderExtended,
  ProductExtended,
  PolicyOrderProduct,
  AddressOriginal,
  PolicyExtendedOriginal,
  PolicyExtended,
} from '@/modules/ecom-policy/types'
import {
  API_ROUTE_ECOM_POLICY_CUSTOMERS,
  API_ROUTE_ECOM_POLICY,
  API_ROUTE_ECOM_POLICY_ORDERS,
  API_ROUTE_ECOM_POLICY_PRICE_LIST,
  API_ROUTE_ECOM_PRODUCT_GROUPS,
  API_ROUTE_ECOM_POLICY_DETAILS,
  API_ROUTE_ECOM_POLICY_VENDORS,
  API_ROUTE_ECOM_POLICY_ORDER_LIST,
  API_ROUTE_ECOM_POLICY_ORDER,
  API_ROUTE_ECOM_POLICY_ORDER_STATUS,
  API_ROUTE_ECOM_POLICY_ORDER_IGNORE,
  API_ROUTE_ECOM_POLICY_ORDER_DOWN_PAYMENTS,
  API_ROUTE_ECOM_POLICY_PRODUCTS,
  API_ROUTE_ECOM_POLICY_PRICE_LIST_FOR_WHOLE_VENDOR_CATALOGUE,
  API_ROUTE_ECOM_POLICY_UPDATE_CATEGORY_CLASSIFICATION,
  API_ROUTE_SHOP_ADDRESSES_SETUP,
  API_ROUTE_ECOM_POLICY_UPDATE_LEASING_VENDOR,
  API_ROUTE_ECOM_POLICY_MANDATORY_PRODUCTS,
  API_ROUTE_ECOM_POLICY_CATEGORY_CLASSIFICATION_LIST,
} from '@/resources/api-routes'
import type { PolicyOriginal } from '@/types'
import { get, post, update } from '@/util/auth'

import type { Classification, CreatePolicyPayload } from './new/types'

const IS_NEW_BACKEND_API = true

export const ecomPolicyResources = {
  getPolicyDetails: async (ecomPolicyId: number) => {
    const policy = await get<PolicyExtendedOriginal>(
      API_ROUTE_ECOM_POLICY_DETAILS.replace(':ecomPolicyId', String(ecomPolicyId))
    )
    const camelCasePolicy = mapKeysToCamelCase(policy)
    const parsedPolicy = convertAttributesToBoolean(camelCasePolicy, [
      'isGlobal',
      'isProductViewAllowedWhenLocked',
      'isProductPriceVisible',
    ])
    return parsedPolicy
  },

  getPolicyProducts: async (policyId: number, policyVendorId: number) => {
    const [products, groupList] = await Promise.all([
      get<ProductExtended[]>(
        API_ROUTE_ECOM_POLICY_PRODUCTS.replace(':ecomPolicyId', String(policyId))
      ),
      get<(Record<string, unknown> & { name: string })[]>(API_ROUTE_ECOM_PRODUCT_GROUPS),
    ])

    const camelizedProducts = mapKeysToCamelCase(products)
    const groupNames = groupList.map(group => group.name)
    const camelizedGroupNames = mapKeysToCamelCase(groupNames)
    const accessoriesByProductId = getAccessoriesByProductId(camelizedProducts)

    // For ecom orders made on policies with vendor_id = 7 (Dustin, Multiconsult), a transportation product needs to be added based on the zip/postal code address.
    // The set of products has category id 15 ("Frakt") and the range of postal codes as part of the product name.
    const transportationProducts =
      policyVendorId === 7 ? camelizedProducts.filter(product => product.catId === 15) : []

    return {
      products: camelizedProducts,
      groupNames: camelizedGroupNames,
      accessoriesByProductId,
      transportationProducts,
    }
  },

  getMandatoryProducts: async (policyId: number, selectedProductIds: number[] = []) => {
    const queryString = selectedProductIds.map(id => `productIds=${id}`).join('&')

    const url =
      API_ROUTE_ECOM_POLICY_MANDATORY_PRODUCTS.replace(':policyId', String(policyId)) +
      '?' +
      queryString

    const response = await get<ProductExtended[]>(url)
    const parsedData = camelizeKeys(response)

    return parsedData as ProductExtended[]
  },

  getOrderList: async () => {
    const orders = await get<unknown>(API_ROUTE_ECOM_POLICY_ORDER_LIST)
    const parsedOrders = camelizeKeys(orders)

    return parsedOrders as PolicyOrder[]
  },

  getCustomerData: async () => {
    const customers = await get<unknown>(API_ROUTE_ECOM_POLICY_CUSTOMERS)
    const parsedCustomers = camelizeKeys(customers) as Customer[]

    const customerHeads = uniqBy(
      (parsedCustomers as Customer[]).map(customer => ({
        id: customer.customerHeadId,
        name: customer.customerHeadName,
        orgNo: customer.customerHeadOrgNo,
      })),
      'id'
    )

    const customerData: {
      customers: Customer[]
      customerHeads: CustomerHead[]
    } = { customers: parsedCustomers, customerHeads }

    return customerData
  },

  getCustomerHeadVendorList: async (customerHeadId: number) => {
    const vendors = await get<unknown>(
      API_ROUTE_ECOM_POLICY_VENDORS.replace(':customerHeadId', String(customerHeadId))
    )
    const parsedVendors = camelizeKeys(vendors)

    return parsedVendors as Vendor[]
  },

  getShopAddressSetup: async (vendorId: number, costObjectId: any) => {
    let url = API_ROUTE_SHOP_ADDRESSES_SETUP.replace(':id', String(vendorId))
    if (costObjectId) {
      url += `?costObjectId=${costObjectId}`
    }

    const addressesSetup = await get<AddressOriginal[]>(url)
    const parsedAddressesSetup = mapKeysToCamelCase(addressesSetup)
    return parsedAddressesSetup
  },

  getCostObjectPolicyList: async (costObjectId: number) => {
    const policies = await get<(PolicyOriginal & { is_locked: 0 | 1 })[]>(
      `${API_ROUTE_ECOM_POLICY}?costObjectId=${costObjectId}`
    )
    const camelCasePolicies = mapKeysToCamelCase(policies)
    const parsedPolicies = camelCasePolicies.map(policy =>
      convertAttributesToBoolean(policy, ['isGlobal', 'isLocked'])
    )

    return parsedPolicies
  },
  getPolicyPriceList: async (policyId: number, customerId?: number) => {
    const productsUrl =
      API_ROUTE_ECOM_POLICY_PRICE_LIST.replace(':policyId', String(policyId)) +
      (customerId ? `?customerId=${customerId}` : '')
    const [response, groupList] = await Promise.all([
      get<Product[]>(productsUrl),
      get<ProductGroup[]>(API_ROUTE_ECOM_PRODUCT_GROUPS),
    ])
    const groupNames = groupList.map(group => group.name)
    const policyPriceList = camelizeKeys({ response, groupNames })

    return policyPriceList as { response: Product[]; groupNames: string[] }
  },

  getPolicyPriceListForWholeVendorCatalogue: async (policyId: number, customerId?: number) => {
    const productsUrl =
      API_ROUTE_ECOM_POLICY_PRICE_LIST_FOR_WHOLE_VENDOR_CATALOGUE.replace(
        ':policyId',
        String(policyId)
      ) + (customerId ? `?customerId=${customerId}` : '')
    const [response, groupList] = await Promise.all([
      get<Product[]>(productsUrl),
      get<ProductGroup[]>(API_ROUTE_ECOM_PRODUCT_GROUPS),
    ])
    const groupNames = groupList.map(group => group.name)
    const policyPriceList = camelizeKeys({ response, groupNames })

    return policyPriceList as { response: Product[]; groupNames: string[] }
  },

  getOrder: async (orderId: number) => {
    const order = await get<unknown>(
      API_ROUTE_ECOM_POLICY_ORDER.replace(':orderId', String(orderId))
    )
    const { products, ...parsedOrder } = camelizeKeys(order) as Omit<
      PolicyOrderExtended,
      'products'
    > & { products: (Omit<PolicyOrderProduct, 'assetIds'> & { assetIds: string | null })[] }
    const parsedProducts = products.map(({ assetIds, ...product }) => ({
      ...product,
      assetIds: assetIds?.split(',').map(id => Number(id)) || null,
    }))

    return {
      ...parsedOrder,
      isGlobalPolicy: Boolean(parsedOrder.isGlobalPolicy),
      products: parsedProducts,
    }
  },

  getCategoryClassification: () =>
    get<{ id: Classification; name: string; isAccessory: boolean }[]>(
      API_ROUTE_ECOM_POLICY_CATEGORY_CLASSIFICATION_LIST,
      IS_NEW_BACKEND_API
    ),

  // mutations
  postShopOrder: (order: Order): Promise<{ orderId: number }> => {
    const { contactPhoneCountryCode, contactPhoneNumber } = order

    let contactMobileNumber = ''

    if (contactPhoneCountryCode && contactPhoneNumber) {
      contactMobileNumber = `${contactPhoneCountryCode}${contactPhoneNumber}`
    }

    return post(API_ROUTE_ECOM_POLICY_ORDERS, { ...order, contactMobileNumber })
  },
  createPolicy: (
    policyData: CreatePolicyPayload & { classificationList: Classification[] | null }
  ): Promise<{ policyId: number }> => post(API_ROUTE_ECOM_POLICY, policyData),

  postPolicyProducts: ({
    policyId,
    payload,
  }: {
    policyId: number
    payload: {
      products: {
        productId: number
        isSelected: boolean
        isMandatory: boolean
        priceId: number | null
        quantityLimit: number
        alias: string | null
        aliasDescription: string | null
      }[]
    }
  }): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_PRODUCTS.replace(':ecomPolicyId', String(policyId)), payload),

  postPolicy: ({
    policyId,
    payload,
  }: {
    policyId: number
    payload: Partial<PolicyExtended>
  }): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_DETAILS.replace(':ecomPolicyId', String(policyId)), payload),

  // Two separate queries to show the proper loading state
  approveOrder: (orderId: number): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_ORDER_STATUS.replace(':orderId', String(orderId)), {
      statusId: ECOM_ORDER_STATUS.NEW,
    }),
  rejectOrder: (orderId: number): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_ORDER_STATUS.replace(':orderId', String(orderId)), {
      statusId: ECOM_ORDER_STATUS.REJECTED,
    }),

  ignoreOrder: (orderId: number): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_ORDER_IGNORE.replace(':orderId', String(orderId))),

  deleteOrderDownPayments: (orderId: number): Promise<null> =>
    post(API_ROUTE_ECOM_POLICY_ORDER_DOWN_PAYMENTS.replace(':orderId', String(orderId))),

  updatePolicyCategoryClassification: ({
    policyId,
    payload,
  }: {
    policyId: number
    payload: {
      categoryClassificationList: Classification[] | null
      isLeasing: boolean
      leasingVendorId: number | null
    }
  }): Promise<Classification[]> =>
    update(
      API_ROUTE_ECOM_POLICY_UPDATE_CATEGORY_CLASSIFICATION.replace(':id', policyId.toString()),
      payload,
      IS_NEW_BACKEND_API
    ),

  updatePolicyLeasingVendor: ({
    policyId,
    vendorId,
  }: {
    policyId: number
    vendorId: number
  }): Promise<null> =>
    post(
      API_ROUTE_ECOM_POLICY_UPDATE_LEASING_VENDOR.replace(':policyId', policyId.toString()),
      {
        vendorId,
      },
      IS_NEW_BACKEND_API
    ),
}

const getAccessoriesByProductId = (productList: ProductExtended[] | undefined) => {
  if (!productList) return

  const accessoriesByProductId = productList.reduce(
    (acc, product) => {
      const parentProductIds = product.parentProductIdList?.split(',').map(id => Number(id))
      if (!parentProductIds) return acc

      parentProductIds?.forEach(parentProductId => {
        acc[parentProductId] = [...(acc[parentProductId] || []), product.id]
      })

      return acc
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<number, number[]>
  )

  return accessoriesByProductId
}

export type Addresses = Awaited<ReturnType<typeof ecomPolicyResources.getShopAddressSetup>>
export type CostObjectPolicy = Awaited<
  ReturnType<typeof ecomPolicyResources.getCostObjectPolicyList>
>[number]
